import { assign, createMachine } from 'xstate';

import { AddressSuggestion, Recipient } from 'api/api';

import { bootstrapConfig } from '~src/context/config';
import { apiInstance } from '~src/global-state';

import { MetaAddressFormFields } from '../types';

type MachineArgs = {
  initialEntry: boolean;
  isBillingPresent: boolean;
  hideAddressBook?: boolean;
  addressBookLength?: number;
  externalAddressBookProvider?: string;
  extendedValidationFlow?: boolean;
};

export const createBillingFlowMachine = ({
  initialEntry,
  isBillingPresent,
  hideAddressBook = false,
  addressBookLength = 0,
  externalAddressBookProvider,
  extendedValidationFlow,
}: MachineArgs) =>
  createMachine(
    {
      predictableActionArguments: true,
      id: 'billing',
      context: {
        initialEntry,
        hideAddressBook,
        addressBookLength,
        currentAddress: undefined,
        externalAddressBookProvider,
        cleanForm: false,
        extendedValidation: {
          address: undefined,
          suggestion: undefined,
          isSubmissionBlocking: false,
        },
      },
      schema: {
        context: {} as Context,
        events: {} as Events,
        services: {} as Services,
      },
      tsTypes: {} as import('./billing-flow-machine.typegen').Typegen0,
      type: 'parallel',
      states: {
        checkout: {
          initial: getInitialFormStateNode(isBillingPresent, externalAddressBookProvider),
          states: {
            billingSummary: {
              initial: 'idle',
              states: {
                idle: {},
                submitting: {
                  invoke: {
                    src: 'updateBillingService',
                    onDone: {
                      target: ['#billing.checkbox.unchecked', 'idle'],
                      actions: 'updateAddressBookLength',
                    },
                    onError: {},
                  },
                },
              },
              on: {
                BUTTON_CLICK: [
                  { cond: 'addressBookHidden', target: 'inlineAddressForm' },
                  { cond: 'editInitialAddress', target: 'inlineAddressForm' },
                  { cond: 'hasOneAddress', target: 'inlineAddressForm' },
                  { target: '#billing.modal.modalAddressList' },
                ],
              },
            },
            billingSameAsRecipient: {
              initial: 'idle',
              states: {
                idle: {},
                submitting: {
                  invoke: {
                    src: 'updateBillingService',
                    onDone: {
                      target: ['#billing.checkbox.checked', 'idle'],
                    },
                    onError: {},
                  },
                },
              },
            },
            inlineAddressForm: {
              initial: 'idle',
              states: {
                idle: {},
                submitting: {
                  tags: 'fetching',
                  invoke: {
                    src: 'updateBillingService',
                    onDone: [
                      {
                        cond: 'isSameAsRecipient',
                        target: '#billing.checkout.billingSummary',
                      },
                      {
                        target: '#billing.checkout.billingSummary',
                        actions: 'updateAddressBookLength',
                      },
                    ],
                  },
                },
              },
              on: { SUBMIT: { target: '.submitting', actions: 'updateCurrentAddress' } },
            },
          },
        },
        modal: {
          initial: 'hidden',
          states: {
            hidden: {},
            modalAddressList: {
              initial: 'idle',
              states: {
                idle: {},
                submitting: {
                  tags: 'fetching',
                  invoke: {
                    src: 'updateBillingService',
                    onDone: {
                      target: ['#billing.modal.hidden', '#billing.checkout.billingSummary'],
                      actions: 'updateAddressBookLength',
                    },
                  },
                },
              },
              on: {
                ADD_NEW_ADDRESS: { target: 'modalAddressForm', actions: 'resetCurrentAddress' },
                OPEN_ADDRESS: { target: 'modalAddressForm', actions: 'updateCurrentAddress' },
                CLOSE: [
                  { cond: () => !!extendedValidationFlow, target: ['hidden'] },
                  { target: ['hidden', '#billing.checkout.billingSummary'] },
                ],
                SUBMIT: [
                  {
                    cond: 'isAddressDirty',
                    target: 'modalAddressForm',
                    actions: 'updateCurrentAddress',
                  },
                  {
                    cond: 'isBillingTheSame',
                    target: ['hidden', '#billing.checkout.billingSummary'],
                  }, //TODO
                  { target: '.submitting', actions: 'updateCurrentAddress' },
                ],
              },
            },
            modalAddressForm: {
              initial: 'idle',
              states: {
                idle: {},
                submitting: {
                  tags: 'fetching',
                  invoke: {
                    src: 'updateBillingService',
                    onDone: [
                      {
                        cond: 'isSameAsRecipient',
                        target: [
                          '#billing.modal.hidden',
                          '#billing.checkout.billingSameAsRecipient',
                          '#billing.checkbox.checked',
                        ],
                        actions: ['updateAddressBookLength', 'resetCurrentAddress'],
                      },
                      {
                        target: [
                          '#billing.modal.hidden',
                          '#billing.checkout.billingSummary',
                          '#billing.checkbox.unchecked',
                        ],
                        actions: ['updateAddressBookLength', 'resetCurrentAddress'],
                      },
                    ],
                  },
                },
                updateBillingAfterDelete: {
                  tags: 'fetching',
                  invoke: {
                    src: 'updateBillingService',
                    onDone: {
                      target: '#billing.modal.modalAddressList',
                      actions: 'updateAddressBookLength',
                    },
                  },
                },
                deleteAddress: {
                  always: [
                    {
                      cond: 'addressBookLongerThanOne',
                      target: 'deletingAddress',
                    },
                    { target: 'idle', actions: assign({ cleanForm: true }) },
                  ],
                },
                deletingAddress: {
                  tags: 'fetching',
                  invoke: {
                    src: 'deleteBillingService',
                    onDone: { target: 'updateBillingAfterDelete' },
                    onError: {},
                  },
                },
              },
              on: {
                SUBMIT: [
                  {
                    cond: 'isExternalProvider',
                    target: '.submitting',
                    actions: ['updateCurrentAddress', assign({ cleanForm: false })],
                  },
                  {
                    cond: 'isBillingTheSame',
                    target: ['hidden', '#billing.checkout.billingSummary'],
                    actions: assign({ cleanForm: false }),
                  },
                  {
                    cond: 'isSameAsRecipient',
                    target: ['hidden', '#billing.checkout.billingSameAsRecipient.submitting'],
                  },
                  {
                    target: '.submitting',
                    actions: ['updateCurrentAddress', assign({ cleanForm: false })],
                  },
                ],
                CLOSE: { target: 'modalAddressList', actions: assign({ cleanForm: false }) },
                DELETE: {
                  cond: 'isNotExternalProvider',
                  target: '.deleteAddress',
                  actions: 'updateCurrentAddress',
                },
              },
            },
            extendedValidationModal: {
              initial: 'idle',
              states: {
                idle: {},
                submitting: {
                  exit: 'clearExtendedValidationAddresses',
                  tags: 'fetching',
                  invoke: {
                    src: 'updateBillingService',
                    onDone: {
                      target: [
                        '#billing.modal.hidden',
                        '#billing.checkout.billingSummary',
                        '#billing.checkbox.unchecked',
                      ],
                      actions: ['updateAddressBookLength', 'resetCurrentAddress'],
                    },
                    onError: {},
                  },
                },
              },
              on: {
                CLOSE: [
                  {
                    cond: 'addressBookHidden',
                    target: 'hidden',
                  },
                  {
                    cond: 'isInitialEntry',
                    target: 'hidden',
                  },
                  {
                    target: '#billing.modal.modalAddressForm',
                  },
                ],
                // HACK: renaming to SUBMIT causes double transition to .submitting state in inline mode
                SUBMIT_ADDRESS: { target: '.submitting' },
              },
            },
          },
        },
        checkbox: {
          initial: getInitialCheckboxStateNode(isBillingPresent, externalAddressBookProvider),
          states: {
            unchecked: {
              on: {
                CHECK: [
                  {
                    cond: 'checkWithoutAddress',
                    target: ['checked', '#billing.checkout.billingSameAsRecipient'],
                  },
                  {
                    cond: 'hasOneAddress',
                    target: ['checked', '#billing.checkout.billingSameAsRecipient'],
                  },
                  {
                    cond: 'addressBookHidden',
                    target: ['checked', '#billing.checkout.billingSameAsRecipient'],
                  },
                  {
                    target: ['#billing.checkout.billingSameAsRecipient.submitting'],
                  },
                ],
              },
            },
            checked: {
              on: {
                //cond if address is dirty
                UNCHECK: [
                  {
                    cond: 'addressBookHidden',
                    target: ['unchecked', '#billing.checkout.inlineAddressForm'],
                  },
                  {
                    cond: 'isInitialEntry',
                    target: ['unchecked', '#billing.checkout.inlineAddressForm'],
                  },
                  {
                    cond: 'hasOneAddress',
                    target: ['unchecked', '#billing.checkout.inlineAddressForm'],
                  },
                  {
                    cond: () => !!extendedValidationFlow,
                    actions: 'updateCurrentAddress',
                    target: ['#billing.modal.modalAddressForm'],
                  },
                  {
                    target: ['#billing.checkout.billingSummary.submitting'],
                  },
                ],
              },
            },
          },
        },
      },
      on: {
        UPDATE_COUNT: { actions: 'updateAddressBookLength' },
        BILLING_SAME_AS_RECIPIENT: {
          target: ['#billing.checkbox.checked', '#billing.checkout.billingSameAsRecipient'],
        },
        SHOW_EXTENDED_VALIDATION_MODAL: {
          target: '#billing.modal.extendedValidationModal',
          actions: [
            'updateCurrentAddress',
            assign((_, e) => ({
              extendedValidation: e.data,
            })),
          ],
        },
      },
    },
    {
      services: {
        deleteBillingService: async ctx => {
          const headers = ctx.externalAddressBookProvider
            ? { headers: { ['x-ingrid-checkout-session-id']: bootstrapConfig.sessionId } }
            : undefined;

          const { data } = await apiInstance.v4.deleteAddressFromAddressBook(
            ctx.currentAddress!.id!,
            headers
          );
          return { address: data };
        },
      },
      actions: {
        clearExtendedValidationAddresses: assign({
          extendedValidation: {
            address: undefined,
            suggestion: undefined,
            isSubmissionBlocking: false,
          },
        }),
        updateAddressBookLength: assign((ctx, e) => ({
          ...ctx,
          addressBookLength: e.data.addressBookLength,
        })),
        updateCurrentAddress: assign((ctx, e) => ({ ...ctx, currentAddress: e.data.address })),
        resetCurrentAddress: assign(ctx => ({ ...ctx, currentAddress: undefined })),
      },
      guards: {
        checkWithoutAddress: (_, e) => !e.data,
        hasOneAddress: ctx => ctx.addressBookLength === 1,
        isInitialEntry: ctx => !!ctx.initialEntry,
        addressBookHidden: ctx => !!ctx.hideAddressBook,
        isExternalProvider: ctx => !!ctx.externalAddressBookProvider,
        isNotExternalProvider: ctx => !ctx.externalAddressBookProvider,
        isAddressDirty: ctx => !!ctx.currentAddress?.is_dirty,
        addressBookLongerThanOne: ctx => ctx.addressBookLength > 1,
      },
    }
  );

const getInitialFormStateNode = (isBillingPresent: boolean, externalAddressBook?: string) => {
  if (!externalAddressBook && isBillingPresent) {
    return 'billingSummary';
  } else {
    return 'billingSameAsRecipient'; // TODO
  }
};

const getInitialCheckboxStateNode = (isBillingPresent: boolean, externalAddressBook?: string) => {
  const res = getInitialFormStateNode(isBillingPresent, externalAddressBook);
  if (res === 'billingSummary') {
    return 'unchecked';
  } else {
    return 'checked';
  }
};

export type Context = {
  initialEntry: MachineArgs['initialEntry'];
  hideAddressBook: MachineArgs['hideAddressBook'];
  externalAddressBookProvider: MachineArgs['externalAddressBookProvider'];
  addressBookLength: number;
  currentAddress?: Recipient;
  cleanForm: boolean;
  extendedValidation: {
    address?: MetaAddressFormFields;
    suggestion?: AddressSuggestion;
    isSubmissionBlocking: boolean;
  };
};

type Events =
  | { type: 'BUTTON_CLICK' }
  | { type: 'OPEN_ADDRESS'; data: { address: Recipient } }
  | { type: 'CLOSE'; data?: { shouldResetForm: boolean } }
  | { type: 'DELETE'; data: { address: Recipient } }
  | { type: 'SUBMIT'; data: { address: Recipient; onError?: () => void } }
  | { type: 'ADD_NEW_ADDRESS'; data: { address: Recipient } }
  | { type: 'CONFIRM' }
  | { type: 'CHECK'; data?: { address: Recipient } }
  | { type: 'UNCHECK'; data: { address: Recipient } }
  | { type: 'UPDATE_COUNT'; data: { addressBookLength: number } }
  | { type: 'BILLING_SAME_AS_RECIPIENT' }
  | { type: 'SUBMIT_ADDRESS'; data: { address: Recipient } }
  | {
      type: 'SHOW_EXTENDED_VALIDATION_MODAL';
      data: {
        address: MetaAddressFormFields;
        suggestion: AddressSuggestion;
        isSubmissionBlocking: boolean;
      };
    };

type Services = {
  updateBillingService: {
    data: { addressBookLength: number };
  };
  deleteBillingService: { data: { address: Recipient } };
};

export type BillingFlowMachine = ReturnType<typeof createBillingFlowMachine>;
